import { NavigateFunction } from 'react-router-dom';
import { NOTIFICATION_EVENTS_MAP } from '../notificationConstants';

// The order detail lives at `control-pedidos/detalle/:orderId` (router.tsx:300 declares the
// `control-pedidos` parent and :324 the `detalle/:orderId` child rendering `OrderDetails`). There is
// NO `/orders/:id` route in the tree: `grep -rn "orders" src/pages/routes/` is empty, so that path
// fell through to the catch-all `<Route path="*" element={<Navigate to="/" />} />` (router.tsx:761)
// and the index redirected to `getRootPath()` (useModules.ts:87) — the user's FIRST module. The
// banner did not open the order AND threw the operator off the screen they were on.
//
// This is preexisting in master: `PushNotificationContext` already navigated to `/orders/` on the
// FCM push click, but on Android it was unreachable because the `Notification` constructor throws
// before getting there. This PR makes it reachable from the socket path, so fixing it here also
// repairs the FCM push click that is broken in production today.
//
// Same-order target for both events on purpose: the payload carries only `eventId` and `orderId`,
// so a `changeOrderStatus` of an already closed order cannot be told apart from a live one. The
// board is where the operator works, and it is the one absolute deep link the app already uses for
// an order detail (CustomerDetailDrawer.tsx:76, CanceledDeliveryByProviderModal.tsx:102) — the
// in-app new-order toast lands on `/control-pedidos` too (useIncomingOrder.tsx:209). `OrderDetails`
// fetches by id on its own and closes with `navigate(-1)`, so the parent list only decides what is
// behind the drawer, and closing it returns the operator to where they were.
//
// KNOWN LIMIT (cannot be fixed from a pure function): `/control-pedidos` is gated by the HISTORIAL
// module in `MODULES_PATH` (constants.ts:84), and `PageWrapper` (components/layout/PageWrapper.tsx,
// rendered by Orders.tsx) redirects to `getRootPath()` when the module is missing. A user without
// HISTORIAL — cashier roles exist — still gets bounced. Suppressing the notification for those
// users belongs to the caller that decides to notify, not here.
const ORDER_DETAIL_PATH_PREFIX = '/control-pedidos/detalle/';

// Both notification click paths end here: the desktop `onclick` handler and the `NOTIFICATION_CLICK`
// message the service worker posts after `notificationclick`. They used to be two copies of the same
// `if` inside `PushNotificationContext`, and with the Android fallback the worker-owned click became
// the ONLY path on Android — two copies would have let Android drift away from desktop in silence.
export const routeFromNotificationData = (
  data: Record<string, string> | undefined,
  navigate: NavigateFunction,
): void => {
  if (!data?.orderId) return;
  if (
    data.eventId === NOTIFICATION_EVENTS_MAP.newOrder.key ||
    data.eventId === NOTIFICATION_EVENTS_MAP.changeOrderStatus.key
  ) {
    navigate(`${ORDER_DETAIL_PATH_PREFIX}${data.orderId}`);
  }
};
